import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, of, shareReplay, tap } from 'rxjs';
import { Environment } from '../../environment/environment.model';

// Who the logged-in user is to this tenant, and user management for /manager.
// Managers come from the Entra app role ROLE_METAHUMAN_MANAGER (shared with Mynte);
// everyone else is managed in /manager. See backend/Access.

export type AccessStatus = 'Aangevraagd' | 'Actief';

export interface AccessMe {
  email: string | null;
  name: string | null;
  // false: user management is not set up for this tenant; every logged-in user has access.
  enabled: boolean;
  isManager: boolean;
  hasAccess: boolean;
  status: AccessStatus | null;
  roles: string[];
  enforcement: boolean;
}

export interface AccessRole {
  code: string;
  name: string;
}

export interface AccessRoles {
  roles: AccessRole[];
  // false: a user has exactly one role.
  multipleRoles: boolean;
}

export interface AccessUser {
  id: string;
  email: string;
  displayName: string | null;
  status: AccessStatus;
  roles: string[];
  linked: boolean;
  createdAt: string;
  lastSeenAt: string | null;
}

// The backend enforces access on every call; a failing /access/me must not block the UI.
const FALLBACK_ME: AccessMe = {
  email: null,
  name: null,
  enabled: false,
  isManager: false,
  hasAccess: true,
  status: null,
  roles: [],
  enforcement: false,
};

@Injectable({
  providedIn: 'root',
})
export class AccessService {
  private readonly httpClient = inject(HttpClient);
  private me$: Observable<AccessMe> | undefined;

  // Last known /access/me, for templates (e.g. the Beheer link in the top bar).
  public readonly me = signal<AccessMe | null>(null);

  constructor(@Inject('env') private environment: Environment) {}

  private get url(): string {
    return `${this.environment.apiUrl}/access`;
  }

  public getMe(): Observable<AccessMe> {
    if (!this.me$) {
      this.me$ = this.httpClient.get<AccessMe>(`${this.url}/me`).pipe(
        catchError((error) => {
          console.error('Could not determine access:', error);
          return of(FALLBACK_ME);
        }),
        tap((me) => this.me.set(me)),
        shareReplay(1),
      );
    }
    return this.me$;
  }

  public getRoles(): Observable<AccessRoles> {
    return this.httpClient.get<AccessRoles>(`${this.url}/roles`);
  }

  public getUsers(status?: AccessStatus): Observable<AccessUser[]> {
    const params: Record<string, string> = status ? { status } : {};
    return this.httpClient.get<AccessUser[]>(`${this.url}/users`, { params });
  }

  public createUser(email: string, displayName: string, roles: string[]): Observable<AccessUser> {
    return this.httpClient.post<AccessUser>(`${this.url}/users`, { email, displayName, roles });
  }

  // For an access request this also grants access.
  public updateUser(id: string, displayName: string | null, roles: string[]): Observable<AccessUser> {
    return this.httpClient.put<AccessUser>(`${this.url}/users/${encodeURIComponent(id)}`, { displayName, roles });
  }

  public deleteUser(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/users/${encodeURIComponent(id)}`);
  }

  // The Dutch `detail` of the backend's ProblemDetails, when there is one.
  public static errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.detail ?? error.error?.title ?? `Er ging iets mis (${error.status}).`;
    }
    return error instanceof Error ? error.message : String(error);
  }
}
